/**
 * Rolex Appointment Tracking Module
 *
 * Questo modulo gestisce il tracciamento degli eventi di prenotazione
 * appuntamenti per la sezione RCPO Bespoke secondo le specifiche Rolex.
 *
 * - Supporta l'attesa asincrona di cookieManager
 * - Verifica il consenso utente prima di ogni tracciamento
 * - Implementa il formato dati richiesto da Rolex
 */

// Mappatura degli scopi dell'appuntamento ai valori di tracking Rolex
const PURPOSE_MAPPING = {
  'discover': 'collection discovery',
  'service': 'watch servicing',
  'trade': 'watch trading'
};

// Giorni della settimana in inglese (richiesti dal formato Rolex)
const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Classe per il tracciamento degli appuntamenti Rolex
 */
class RolexAppointmentTracking {
  constructor() {
    // Flag per tracciare lo stato del modulo
    this.isRolexPage = this.checkRolexContext();
    this.cookieManagerReady = false;
    this.trackingConsent = false;
    this.isInitialized = false;

    // Parametri URL
    this.isModifyMode = false;
    this.isDeleteMode = false;

    // Inizializza l'attesa di cookieManager e il rilevamento parametri URL
    this.init();
  }

  /**
   * Inizializza il modulo di tracciamento
   */
  init() {
    // Rileva i parametri URL
    this.checkURLParameters();

    // Attendi che cookieManager sia disponibile
    this.waitForCookieManager();

    // Notifica che il modulo è stato inizializzato
    this.isInitialized = true;
    console.log('RolexTracking: modulo inizializzato');
  }

  /**
   * Verifica se siamo in un contesto Rolex
   * @returns {boolean} True se siamo in una pagina Rolex
   */
  checkRolexContext() {
    // Verifica se siamo in una pagina Rolex (Bespoke, CPO o Calendario)
    return (
      document.body.classList.contains('body-rolex-bespoke-v2') ||
      document.body.classList.contains('body-rolex-cpo-v2') ||
      document.getElementById('rolex-appointment-calendar') !== null
    );
  }

  /**
   * Attende che cookieManager diventi disponibile
   */
  waitForCookieManager() {
    console.log('RolexTracking: in attesa di cookieManager...');

    // Se cookieManager è già disponibile
    if (window.cookieManager) {
      this.cookieManagerReady = true;
      this.trackingConsent = this.checkTrackingConsent();
      this.emitReadyEvent();
      return;
    }

    // Inizializza il polling per cookieManager
    const poll = setInterval(() => {
      if (window.cookieManager) {
        clearInterval(poll);
        clearTimeout(timeout);

        this.cookieManagerReady = true;
        this.trackingConsent = this.checkTrackingConsent();

        console.log('RolexTracking: cookieManager trovato, consenso =', this.trackingConsent);
        this.emitReadyEvent();
      }
    }, 200);

    // Timeout di sicurezza (5 secondi)
    const timeout = setTimeout(() => {
      clearInterval(poll);
      console.warn('RolexTracking: cookieManager non trovato dopo 5 secondi');

      // Verifica comunque se Cookiebot è disponibile
      if (typeof Cookiebot !== 'undefined') {
        this.cookieManagerReady = true;
        this.trackingConsent = Cookiebot.consent.statistics;
        console.log('RolexTracking: utilizzando Cookiebot, consenso =', this.trackingConsent);
        this.emitReadyEvent();
      } else {
        // Informa comunque che il modulo è pronto, anche se non può tracciare
        this.emitReadyEvent(false);
      }
    }, 5000);
  }

  /**
   * Emette evento personalizzato quando il modulo è pronto
   * @param {boolean} canTrack - Se il tracciamento è possibile
   */
  emitReadyEvent(canTrack = true) {
    const event = new CustomEvent('rolexTrackingReady', {
      detail: {
        consent: this.trackingConsent,
        rolex: this.isRolexPage,
        canTrack: canTrack && this.isRolexPage && this.trackingConsent
      }
    });
    window.dispatchEvent(event);
  }

  /**
   * Verifica se l'utente ha dato il consenso al tracciamento
   * @returns {boolean} True se il consenso è stato dato
   */
  checkTrackingConsent() {
    // Se cookieManager non è pronto, restituisci lo stato attuale
    if (!this.cookieManagerReady) {
      return this.trackingConsent;
    }

    // Verifica consenso tramite cookieManager
    if (window.cookieManager) {
      return window.cookieManager.getCookie('mc_statistics') === 'true' ||
             window.cookieManager.getCookie('rlx-consent') === 'true';
    }

    // Verifica consenso tramite Cookiebot se disponibile
    if (typeof Cookiebot !== 'undefined') {
      return Cookiebot.consent.statistics;
    }

    return false;
  }

  /**
   * Controlla i parametri URL per rilevare modalità di modifica o cancellazione
   */
  checkURLParameters() {
    const urlParams = new URLSearchParams(window.location.search);
    const appointmentId = urlParams.get('appointment_id');
    const token = urlParams.get('token');
    const action = urlParams.get('action');

    if (appointmentId && token) {
      // Modalità modifica (ha appointment_id e token ma non action=delete)
      if (!action) {
        this.isModifyMode = true;
        console.log('RolexTracking: rilevata modalità modifica appuntamento');
      }
      // Modalità cancellazione (ha appointment_id, token e action=delete)
      else if (action === 'delete') {
        this.isDeleteMode = true;
        console.log('RolexTracking: rilevata modalità cancellazione appuntamento');
      }
    }

    // Supporto per i parametri standard Rolex menzionati nel PDF
    if (urlParams.get('cmpid') === 'email_modify-appointment') {
      this.isModifyMode = true;
      console.log('RolexTracking: rilevato parametro standard modifica appuntamento');
    }

    if (urlParams.get('cmpid') === 'email_cancel-appointment') {
      this.isDeleteMode = true;
      console.log('RolexTracking: rilevato parametro standard cancellazione appuntamento');
    }
  }

  /**
   * Imposta le informazioni dell'appuntamento nel formato richiesto da Rolex
   * @param {Object} appointment - Oggetto con i dati dell'appuntamento
   */
  setAppointmentInformation(appointment) {
    // Aggiorna il consenso per sicurezza
    if (this.cookieManagerReady) {
      this.trackingConsent = this.checkTrackingConsent();
    }

    // Verifica se possiamo tracciare
    if (!this.isRolexPage || !this.trackingConsent) {
      console.log('RolexTracking: impossibile impostare informazioni appuntamento - tracciamento non consentito');
      return null;
    }

    try {
      const { purpose, date, time } = appointment;

      // Verifica che tutti i dati necessari siano presenti
      if (!purpose || !date) {
        console.error('RolexTracking: dati appuntamento incompleti', appointment);
        return null;
      }

      // Converti la stringa data in oggetto Date
      const appointmentDate = new Date(date);
      if (isNaN(appointmentDate.getTime())) {
        console.error('RolexTracking: data appuntamento non valida', date);
        return null;
      }

      // Ottieni il giorno della settimana in inglese
      const dayOfWeek = DAYS_OF_WEEK[appointmentDate.getDay()];

      // Formatta la data come YYYY-MM-DD
      const formattedDate = appointmentDate.toISOString().split('T')[0];

      // Ottieni l'ora nel formato richiesto
      const formattedTime = typeof time === 'string' ? time : '00:00';

      // Mappa lo scopo al formato Rolex
      let mappedPurpose = purpose;
      if (typeof purpose === 'object' && purpose.hasOwnProperty('id')) {
        // Se purpose è un oggetto (dal tuo store), usa il suo id per la mappatura
        mappedPurpose = PURPOSE_MAPPING[purpose.id] || 'collection discovery';
      } else if (PURPOSE_MAPPING[purpose]) {
        // Se purpose è una stringa che corrisponde a una chiave nella mappatura
        mappedPurpose = PURPOSE_MAPPING[purpose];
      }

      // Formatta le informazioni secondo lo schema richiesto: purpose|dayOfWeek|YYYY-MM-DD|hh:mm
      window.appointmentInformation = `${mappedPurpose}|${dayOfWeek}|${formattedDate}|${formattedTime}`;

      console.log('RolexTracking: informazioni appuntamento impostate =', window.appointmentInformation);

      return window.appointmentInformation;
    } catch (error) {
      console.error('RolexTracking: errore durante l\'impostazione delle informazioni appuntamento', error);
      return null;
    }
  }

  /**
   * Verifica se _satellite è disponibile
   * @returns {boolean} True se _satellite è disponibile
   */
  isSatelliteAvailable() {
    return typeof _satellite !== 'undefined';
  }

  /**
   * Traccia un evento generico
   * @param {string} eventName - Nome dell'evento
   * @param {Object} [appointmentData] - Dati appuntamento (opzionale)
   * @returns {boolean} True se l'evento è stato tracciato
   */
  trackEvent(eventName, appointmentData = null) {
    // Aggiorna il consenso per sicurezza
    if (this.cookieManagerReady) {
      this.trackingConsent = this.checkTrackingConsent();
    }

    // Verifica se possiamo tracciare
    if (!this.isRolexPage || !this.trackingConsent) {
      console.log(`RolexTracking: impossibile tracciare evento '${eventName}' - tracciamento non consentito`);
      return false;
    }

    // Se ci sono dati appuntamento, impostali
    if (appointmentData) {
      this.setAppointmentInformation(appointmentData);
    }

    // Verifica se _satellite è disponibile
    if (!this.isSatelliteAvailable()) {
      console.error(`RolexTracking: impossibile tracciare evento '${eventName}' - _satellite non disponibile`);
      return false;
    }

    // Se è un evento che richiede window.appointmentInformation, verifica che sia impostato
    if (['bookAppointment', 'modifyAppointment'].includes(eventName) && !window.appointmentInformation) {
      console.error(`RolexTracking: impossibile tracciare evento '${eventName}' - appointmentInformation non impostato`);
      return false;
    }

    // Traccia l'evento
    try {
      console.log(`RolexTracking: tracciamento evento '${eventName}'`);
      _satellite.track(eventName);
      return true;
    } catch (error) {
      console.error(`RolexTracking: errore durante il tracciamento dell'evento '${eventName}'`, error);
      return false;
    }
  }

  /**
   * Traccia l'evento di prenotazione appuntamento
   * @param {Object} appointment - Oggetto con i dati dell'appuntamento
   */
  trackBookAppointment(appointment) {
    return this.trackEvent('bookAppointment', appointment);
  }

  /**
   * Traccia l'evento di modifica appuntamento
   * @param {Object} appointment - Oggetto con i dati dell'appuntamento
   */
  trackModifyAppointment(appointment) {
    return this.trackEvent('modifyAppointment', appointment);
  }

  /**
   * Traccia l'evento di nuovo appuntamento dopo cancellazione
   */
  trackNewAppointment() {
    return this.trackEvent('newAppointment');
  }

  /**
   * Traccia l'eliminazione di un appuntamento
   */
  trackCancelAppointment() {
    // Il PDF non specifica un evento dedicato per la cancellazione
    // Per completezza, utilizziamo lo stesso evento di newAppointment
    return this.trackEvent('newAppointment');
  }

  /**
   * Forza un ricontrollo del consenso
   * @returns {boolean} Nuovo stato del consenso
   */
  refreshConsent() {
    this.trackingConsent = this.checkTrackingConsent();
    return this.trackingConsent;
  }

  /**
   * Restituisce lo stato corrente del modulo
   */
  getStatus() {
    return {
      isRolexPage: this.isRolexPage,
      cookieManagerReady: this.cookieManagerReady,
      trackingConsent: this.trackingConsent,
      isModifyMode: this.isModifyMode,
      isDeleteMode: this.isDeleteMode,
      satelliteAvailable: this.isSatelliteAvailable(),
      appointmentInformation: window.appointmentInformation || null
    };
  }
}

// Esporta una singola istanza per tutta l'applicazione
export default new RolexAppointmentTracking();